import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  BillingWorktimePanelDto,
  WorktimeGridDto,
  WorktimeOverrideRequest,
  CapacityOverrideRequest,
  WorktimeAssignmentPreviewDto
} from '../models/worktime.model';

/**
 * Angular service for the Worktime REST API.
 *
 * Endpoints:
 *  - GET  /api/v1/worktime/{workingGroupId}?month=X&year=Y
 *  - PUT  /api/v1/worktime/override
 *  - DELETE /api/v1/worktime/override
 */
@Injectable({ providedIn: 'root' })
export class WorktimeApiService {

  private readonly baseUrl = `${environment.apiUrl}/worktime`;

  constructor(private http: HttpClient) { }

  /**
   * Fetch the full worktime grid for a working group + month.
   */
  getWorktimeGrid(workingGroupId: number, month: number, year: number): Observable<WorktimeGridDto> {
    const params = new HttpParams()
      .set('month', month.toString())
      .set('year', year.toString());

    return this.http.get<WorktimeGridDto>(`${this.baseUrl}/${workingGroupId}`, { params });
  }

  getBillingWorktimePanel(
    invoicingGroupId: number,
    groupCombinationId: number,
    employeeId: number,
    month: number,
    year: number,
    category: string | null,
    snapshotId?: number | null,
  ): Observable<BillingWorktimePanelDto> {
    let params = new HttpParams()
      .set('invoicingGroupId', invoicingGroupId.toString())
      .set('groupCombinationId', groupCombinationId.toString())
      .set('employeeId', employeeId.toString())
      .set('month', month.toString())
      .set('year', year.toString());

    if (category) {
      params = params.set('category', category);
    }
    if (snapshotId != null) {
      params = params.set('snapshotId', snapshotId.toString());
    }

    return this.http.get<BillingWorktimePanelDto>(`${this.baseUrl}/panel`, { params });
  }

  /**
   * Create or update a worktime override for a single grid cell.
   */
  saveOverride(request: WorktimeOverrideRequest): Observable<WorktimeOverrideRequest> {
    return this.http.put<WorktimeOverrideRequest>(`${this.baseUrl}/override`, request);
  }

  /**
   * Delete a worktime override (revert cell to computed value).
   */
  deleteOverride(request: WorktimeOverrideRequest): Observable<void> {
    return this.http.request<void>('DELETE', `${this.baseUrl}/override`, { body: request });
  }

  /**
   * Apply a capacity percentage override across a date range.
   * The backend computes baseHours × (percentage / 100) for each weekday.
   * Returns the number of days affected.
   */
  applyCapacityOverride(request: CapacityOverrideRequest): Observable<number> {
    return this.http.put<number>(`${this.baseUrl}/capacity-override`, request);
  }

  /**
   * Import training absences from an uploaded Excel (.xlsx) file.
   * Backend parses the file and creates TRAINING absence records.
   */
  importTraining(
    invoicingGroupId: number,
    file: File,
  ): Observable<{ imported: number; skipped: number; errors: string[] }> {
    const formData = new FormData();
    formData.append('file', file);
    const params = new HttpParams().set('invoicingGroupId', invoicingGroupId.toString());
    return this.http.post<{ imported: number; skipped: number; errors: string[] }>(
      `${this.baseUrl}/import-training`,
      formData,
      { params },
    );
  }

  /**
   * Manually trigger the training batch import from MinIO.
   * Scans pending folder, processes all .xlsx files, and archives them.
   */
  triggerTrainingBatchSync(): Observable<any[]> {
    return this.http.post<any[]>(`${this.baseUrl}/batch/trigger-training-sync`, {});
  }
  /**
   * Manually trigger the automatic Pool assignment batch.
   */
  triggerPoolBatchSync(): Observable<number> {
    return this.http.post<number>(`${this.baseUrl}/batch/trigger-pool-sync`, {});
  }
  // ── Delete Assignment from Worktime ──────────────────────────────────────

  /**
   * Fetch preview of combinations to be removed (for the confirmation modal).
   */
  getEmployeeAssignmentPreview(
    invoicingGroupId: number,
    employeeId: number,
    month: number,
    year: number,
  ): Observable<WorktimeAssignmentPreviewDto[]> {
    const params = new HttpParams()
      .set('month', month.toString())
      .set('year', year.toString());
    return this.http.get<WorktimeAssignmentPreviewDto[]>(
      `${this.baseUrl}/${invoicingGroupId}/employee/${employeeId}/assignments`,
      { params },
    );
  }

  /**
   * Delete all assignments for an employee under an invoicing group for a given month.
   */
  deleteEmployeeAssignments(
    invoicingGroupId: number,
    employeeId: number,
    month: number,
    year: number,
  ): Observable<void> {
    const params = new HttpParams()
      .set('month', month.toString())
      .set('year', year.toString());
    return this.http.delete<void>(
      `${this.baseUrl}/${invoicingGroupId}/employee/${employeeId}/assignments`,
      { params },
    );
  }

  /**
   * Delete a single combination assignment for an employee.
   */
  deleteCombinationAssignment(
    invoicingGroupId: number,
    employeeId: number,
    combinationId: number,
    month: number,
    year: number,
  ): Observable<void> {
    const params = new HttpParams()
      .set('month', month.toString())
      .set('year', year.toString());
    return this.http.delete<void>(
      `${this.baseUrl}/${invoicingGroupId}/employee/${employeeId}/combination/${combinationId}`,
      { params },
    );
  }
}
